/**
 * What bounds a family's growth: how fast it may invite (issue #187), and how large it may
 * get (issue #251).
 *
 * The two are different controls and neither substitutes for the other. A daily allowance
 * bounds **velocity** — how much mail leaves here in a day. It says nothing about
 * **accumulation**: 20 a day against a 14-day expiry means a family could hold 280 live
 * invitations at once, each one a credential that admits its holder to everything the family
 * has ever told Boone, retrospectively and with no way to un-share it (#115 ends access, not
 * memory). And nothing bounded the roster at all.
 *
 * They share this file because the numbers interact — see {@link FAMILY_LIMITS} on why the
 * standing bound is the one a family issuing in bulk meets first — and a reader deciding
 * whether either is safe needs the other in view.
 *
 * ── The daily allowances (issue #187) ─────────────────────────────────────────────────
 *
 * `invite_member` is the only tool that puts a family's request into a stranger's inbox,
 * and until now nothing bounded how often. Owners-only answers *who may invite*; nothing
 * answered *how many*, which is the question an abuse review actually asks — a field that
 * exists so one person can reach another person's inbox is the field that can be pointed
 * at an inbox that never asked.
 *
 * **Why two allowances and not one.** A per-family cap is multiplied by however many
 * families someone belongs to, and the schema models N memberships per user
 * (ADR 0003 decision 4; #13 is the open spike on the interaction design). So the personal
 * allowance is summed across every family the user can invite from, and whichever
 * allowance binds first refuses.
 *
 * **What this does not close.** Neither cap stops one person holding several identities —
 * every new identity is a new `userId` with its own personal allowance. Raising the cost of
 * an identity is signup-side, and the account-level backstop is #118. This bounds what one
 * account can do, which is worth having and is not the whole story.
 *
 * It lives in `@pilotfish/core` for the same reason {@link roleHasCapability} does
 * (issue #107): `services/mcp` asks it before proposing, and `@pilotfish/db` asks it again
 * as a condition on the write that applies. A rule enforced only at the first of a two-call
 * write is a rule the caller can hold proposals to escape.
 */

/** The two daily allowances, in invitations per UTC day. */
export interface InviteAllowances {
  /** Across one family, whoever sends them. */
  readonly perFamily: number;
  /** Across one person, summed over every family they can invite from. */
  readonly perUser: number;
}

/**
 * The pilot's numbers, deliberately erring high.
 *
 * The two failure modes are not symmetric: a cap set too low blocks a real family from
 * growing and is felt immediately by someone doing nothing wrong, while a cap set too
 * high still bounds the damage to something small and slow. A household planning unit is
 * a handful of people (`CLAUDE.md`: co-parents living apart are two families), and even
 * allowing for re-sends after a typo or a lost email, no plausible family approaches
 * twenty invitations in a day.
 *
 * `perUser` sits below `perFamily` so that the personal allowance is what binds for the
 * common single-owner family, and `perFamily` catches a family with several owners
 * sending in concert. Tightening either is changing a number here and nothing else.
 */
export const INVITE_ALLOWANCES: InviteAllowances = {
  perFamily: 20,
  perUser: 15,
};

/** How many invitations have already been sent today, on each axis. */
export interface InviteUsage {
  readonly family: number;
  readonly user: number;
}

/**
 * Whether one more invitation is within both allowances.
 *
 * Deliberately answers a bare boolean rather than *which* allowance was reached: the
 * refusal a caller sees must read the same either way, because a message that distinguishes
 * them tells someone probing the limits which dimension to spread across.
 */
export function withinInviteAllowances(
  usage: InviteUsage,
  allowances: InviteAllowances = INVITE_ALLOWANCES,
): boolean {
  return usage.family < allowances.perFamily && usage.user < allowances.perUser;
}

// ── The standing bounds (issue #251) ────────────────────────────────────────────────────

/** How large one family may get, and how many unanswered invitations it may hold. */
export interface FamilyLimits {
  /** Active members, counting the places already promised to unanswered invitations. */
  readonly maxMembers: number;
  /** Invitations sent, unanswered and not yet expired — live credentials, in other words. */
  readonly maxPendingInvitations: number;
}

/**
 * **Both are safety rails, and neither is a price.** [ADR 0022](../../../../docs/adr/0022-payments-who-the-customer-is.md)
 * decision 3 settles that: what is sold is the conversation we host, everything a family
 * remembers is free, and `resolveFamily` and `roleHasCapability` never read a subscription —
 * so no number in this file has any pricing meaning and none may acquire one. This paragraph
 * used to open *"`maxMembers` is the free tier's ceiling"*, written before that ADR and
 * contradicted by it; `CLAUDE.md` has flagged the sentence as wrong ever since, and #358
 * deletes it rather than leaving a reader to arbitrate between a comment and an ADR.
 *
 * They are still enforced in different places, which is the thing worth knowing here:
 *
 * - the seat ceiling is a **conditional write** (`buildMemberSeatClaim`), taken in the
 *   transaction that creates a membership, so it is exact and a plan can bill on it;
 * - the pending bound is a **snapshot count** before the write, because an invitation stops
 *   being live by expiring and no write accompanies that — a counter could only ever climb.
 *   Nobody bills on it and one or two over under concurrency costs nothing.
 *
 * {@link limitReachedByInvite} takes its limits as a **parameter**, defaulted here, so the day
 * a per-family number is wanted the change is a lookup at the call sites that already thread
 * them. What must not happen is a second check appearing beside this one.
 *
 * Six is the size of one shared planning unit. A `FAMILY#` is exactly that (`CLAUDE.md`), so
 * it covers a couple, their children and a grandparent living in; a crew or a wedding party
 * runs past it, and is not what a family tenant is for. Ten unanswered invitations is how
 * many anybody should have out at once — each one is a live credential admitting its holder
 * to everything the family has ever told Boone.
 *
 * The strategy behind the numbers lives in issue #251 and CLAUDE.md rather than here.
 */
export const FAMILY_LIMITS: FamilyLimits = {
  maxMembers: 6,
  maxPendingInvitations: 10,
};

/** What a family currently holds. Counted live from the rows — see `packages/db/src/capacity.ts`. */
export interface FamilySize {
  readonly activeMembers: number;
  /** Pending **and** unexpired. An expired invitation admits nobody, so it holds no place. */
  readonly pendingInvitations: number;
}

/** Which standing bound an invitation would cross. */
export type FamilyLimit = "members" | "pending_invitations";

/**
 * The bound one more invitation would cross, or `null` if it crosses neither.
 *
 * **It names which one, where {@link withinInviteAllowances} deliberately refuses to.** That
 * is not an inconsistency: the daily allowances hide their axis because a message
 * distinguishing them tells somebody probing the limits which dimension to spread across,
 * whereas both of these are already on `show_roster` — a family can count their own members
 * and their own outstanding invitations whenever they like. Naming the bound discloses
 * nothing they cannot already read, and buys the difference between a refusal a family can
 * act on and one they can only be baffled by, since the remedies differ.
 *
 * **The seat bound is answered first when both are reached**, because it is the general
 * refusal and its message names *every* remedy — cancel an invitation, wait for one to
 * expire, or take a member out. That ordering is what makes the pending bound safe to keep
 * while it is dormant: at six seats it can only ever coincide with the seat bound, and
 * coinciding means it is never the thing reported. It answers only when it binds **alone**,
 * which is exactly the state its narrower advice is right for — a family with seats to spare
 * that is nonetheless holding ten unredeemed credentials.
 */
export function limitReachedByInvite(
  size: FamilySize,
  limits: FamilyLimits = FAMILY_LIMITS,
): FamilyLimit | null {
  // Members only. Outstanding invitations used to be added here, back when this was where
  // the ceiling was enforced and it had to account for the ones already out (#251). The
  // ceiling is now a conditional write at acceptance, so this is guidance and an invitation
  // is not a member (PR #268 review).
  if (size.activeMembers >= limits.maxMembers) return "members";
  if (size.pendingInvitations >= limits.maxPendingInvitations) return "pending_invitations";
  return null;
}

// ── The per-person bound (issue #358) ───────────────────────────────────────────────────

/** How many households one person may belong to. */
export interface PersonLimits {
  /** Active memberships, across every family. */
  readonly maxHouseholds: number;
}

/**
 * **Twenty households per person** ([ADR 0022](../../../../docs/adr/0022-payments-who-the-customer-is.md)
 * decision 2a), and the number had no code behind it until #358.
 *
 * **It is on the person's axis, not the family's, and that is why it is not a
 * {@link FamilyLimits} field.** {@link FAMILY_LIMITS} bounds what one family accumulates;
 * this bounds what one *person* accumulates across families, which is the axis
 * {@link InviteAllowances} already models with `perUser` and for the same reason — a
 * per-family number is multiplied by however many families somebody belongs to, so it can
 * never answer a question about the person.
 *
 * **A safety bound, not a plan.** Nothing raises it, nothing sells it, and no code that
 * reads it may read a subscription (ADR 0022 decision 3). What it protects against is the
 * only thing a per-person ceiling can protect against: one account quietly minting
 * households, each of which is a partition, an audit trail and an invitation allowance of
 * its own. Twenty is far past any real answer to *"how many separate groups do you plan
 * with?"* — `CLAUDE.md`'s own examples reach two — so the number is chosen to be
 * unreachable by the people it is not aimed at, exactly as {@link INVITE_ALLOWANCES} is.
 *
 * **Enforced as a transactional condition, never as a count taken first** (`packages/db`'s
 * `households.ts`). A pre-write count is a snapshot, and #251 walked ten concurrent
 * acceptances straight through one; the same review applies here, one partition over.
 */
export const PERSON_LIMITS: PersonLimits = {
  maxHouseholds: 20,
};
